import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as parsedOrderService from "@/supabase/parsedorder/parsedOrderService";
import * as orderService from "@/supabase/order/service/orderService";
import {
  buildExpectedItemsPayload,
  classifyShippingStale,
  ExpectedItemTuple,
} from "@/utils/shippingStaleClassify";
import { ShippingSyncApplyStatus } from "@/utils/shippingSyncResponse";
import { logError } from "@/lib/sentry";

// 배송엑셀 stale 상품명 일괄 반영 — 스캔·분류·반영 훅 (플랜 codexloop 합의, 2026-07-29).
// UI 상태(모달 open/체크박스)는 컨테이너가 소유하고, 이 훅은 데이터만 담당한다.
// 규율: service만 import (repository·supabase client 직접 import 금지).
//
// 스코프 결속 (PR2 codexloop R1-#1·#4 수용):
//  · 후보는 스캔을 만든 스코프(userId|orderDate|gate)에 결속된다. 스캔 시작 시점에 후보를
//    즉시 비워 이전 스코프의 후보가 새 스코프에서 actionable로 남지 않게 한다.
//  · 모든 입력은 deps 없는 useLayoutEffect의 latest-ref로 읽는다 — 구 클로저의 재스캔이
//    현재 스코프를 덮어쓸 수 없다. setState 전에 "시작 시점 스코프 == 현재 스코프"를 재확인.
//
// 안전 계약(전부 PR1 코어에 정의된 것을 소비):
//  · 스캔 무결성 검증(verified) 실패 = 'failed' — 잘린 데이터로 제안하지 않는다.
//  · 반영은 배치 CAS RPC 1회. 성공 후에도 "검증 스캔"이 끝날 때까지 busy를 유지한다
//    (PR2 R1-#2 수용 — RPC 트랜잭션 이후 항목 변경이 있었으면 행이 다시 나타난다).
//  · envelope 검증 실패/네트워크 오류 = commit-ambiguous → 후보 즉시 폐기 + 복구 스캔.
//    복구 스캔은 reconcile 모드로 페이지 rows까지 DB 값으로 맞춘다(R1-#3 수용 —
//    "DB는 커밋됐는데 응답만 유실"이면 배너 없이 로컬 rows만 stale로 남는 홀 차단).
//  · modified_at null 행은 CAS 기준값이 없어 반영 불가(applyEligible=false).
//  · 한 번에 반영 가능한 행 수는 RPC 계약상 500 — 호출부(모달)가 캡 초과를 막는다.

export const SHIPPING_SYNC_APPLY_CAP = 500;

export type StaleCandidateKind =
  | "prefix_compatible"
  | "other_difference"
  | "blank"
  | "zero_item_with_stored";

export interface StaleCandidate {
  parsedOrderId: number;
  orderId: number;
  nickname: string;
  stored: string | null;
  synthesized: string;
  kind: StaleCandidateKind;
  /** 체크박스 초기값 — prefix_compatible 중 suffix created_at 휴리스틱 통과분만 true */
  defaultChecked: boolean;
  /** false = 반영 자체가 불가(zero_item 경고 행, modified_at null 행) */
  applyEligible: boolean;
  modifiedAt: string | null;
  expectedItems: ExpectedItemTuple[];
  /** 같은 order_id에 배송정보 행이 2개 이상 (프로드 실존) — UI 인디케이터용 */
  duplicateOrderId: boolean;
  /** 직전 반영 시도의 행별 결과 (conflict 행 표시용) */
  applyStatus?: ShippingSyncApplyStatus;
}

export type StaleScanState = "idle" | "scanning" | "ready" | "failed";

export interface ApplyOutcome {
  applied: number;
  conflicts: number;
  failed: number;
  appliedRows: { parsedOrderId: number; newValue: string }[];
}

/**
 * apply 결과 3분기 — 컨테이너가 각각 다르게 반응해야 해서 null 하나로 뭉개지 않는다.
 *  done        — RPC 완료 + 검증 스캔 완료. outcome 참조.
 *                verifyFailed=true면 검증 스캔이 무결성 실패 — 모달 뒤에 실패 배너가 숨지 않게
 *                컨테이너가 모달을 닫아야 한다.
 *  ambiguous   — commit-ambiguous. recovered=false면 복구 스캔조차 실패(failed 상태).
 *  stale-scope — 반영 도중 스코프(날짜/유저) 변경·언마운트·인플라이트 중복 호출. UI 갱신 없음.
 */
export type ApplyResult =
  | { type: "done"; outcome: ApplyOutcome; verifyFailed: boolean }
  | { type: "ambiguous"; recovered: boolean }
  | { type: "stale-scope" };

export interface UseShippingStaleSyncProps {
  userId: string | null;
  /** yyyy-MM-dd — 배송엑셀에서 선택된 날짜 */
  orderDate: string;
  /** 게이트: false면 스캔 자체를 하지 않는다 (정산서 합성이 source of truth가 아님) */
  parseProductName: boolean;
  /**
   * 같은 스코프에서 후보 신선도에 영향 주는 페이지측 변경(인라인 상품명 저장 등)이 있을 때
   * 증가시키는 카운터 — 변경 시 재스캔한다. (PR 리뷰 P1: 스코프 동일 변경 미감지)
   */
  refreshToken?: number;
  /** 반영 성공 행을 페이지 rows에 패치 (존재하는 id만 갱신할 것) */
  onApplied?: (rows: { parsedOrderId: number; newValue: string }[]) => void;
  /**
   * commit-ambiguous 복구 스캔이 읽은 DB 확정값으로 페이지 rows를 재조정.
   * 일반 스캔에서는 호출되지 않는다 — 인라인 편집 등 다른 로컬 쓰기와의 충돌 면을 줄이기 위해
   * 복구 경로에서만 동기화한다.
   */
  onReconciled?: (rows: { parsedOrderId: number; productName: string | null }[]) => void;
}

export interface UseShippingStaleSyncResult {
  scanState: StaleScanState;
  candidates: StaleCandidate[];
  isApplying: boolean;
  rescan: () => void;
  apply: (parsedOrderIds: number[]) => Promise<ApplyResult>;
}

interface ScanScope {
  userId: string | null;
  orderDate: string;
  parseProductName: boolean;
}

const scopeKeyOf = (s: ScanScope): string =>
  `${s.userId ?? ""}|${s.orderDate}|${s.parseProductName}`;

export function useShippingStaleSync({
  userId,
  orderDate,
  parseProductName,
  refreshToken = 0,
  onApplied,
  onReconciled,
}: UseShippingStaleSyncProps): UseShippingStaleSyncResult {
  const [scanState, setScanState] = useState<StaleScanState>("idle");
  const [candidates, setCandidates] = useState<StaleCandidate[]>([]);
  const [isApplying, setIsApplying] = useState(false);

  // latest-ref 규율(deps 없는 useLayoutEffect) — 커밋~effect 창 없이 항상 최신 입력을 읽는다.
  const scopeRef = useRef<ScanScope>({ userId, orderDate, parseProductName });
  const onAppliedRef = useRef(onApplied);
  const onReconciledRef = useRef(onReconciled);
  useLayoutEffect(() => {
    scopeRef.current = { userId, orderDate, parseProductName };
    onAppliedRef.current = onApplied;
    onReconciledRef.current = onReconciled;
  });

  // request token — 늦게 도착한 스캔 결과 폐기. 인플라이트 apply 원자 가드는 별도 ref.
  const scanSeqRef = useRef(0);
  const applyInFlightRef = useRef(false);
  // 언마운트(로그아웃 등) 후 in-flight apply 연속이 콜백/스캔/토스트를 되살리지 못하게 하는
  // 가드. StrictMode의 mount→cleanup→re-mount에서 같은 ref가 재사용되므로 본문에서 false 리셋.
  const disposedRef = useRef(false);
  useEffect(() => {
    disposedRef.current = false;
    return () => {
      disposedRef.current = true;
      // 죽은 인스턴스에 좌초된 refresh 요청이 남지 않게 정리 (StrictMode 재마운트 시
      // 본문에서 disposed=false로 되살아나며 스코프 effect가 어차피 새로 스캔한다).
      pendingRefreshRef.current = false;
    };
  }, []);

  /**
   * 항상 "현재 스코프"를 스캔한다(구 클로저 없음 — 입력은 scopeRef에서 읽음).
   * reconcile=true면 스캔이 읽은 DB 확정값으로 페이지 rows 재조정 콜백까지 호출.
   * 반환 = 이 스캔의 종결 상태. 'stale' = seq/스코프/디스포즈에 밀려 결과가 버려짐.
   */
  const runScan = useCallback(
    async (options?: { reconcile?: boolean }): Promise<"ready" | "failed" | "idle" | "stale"> => {
    const seq = ++scanSeqRef.current;
    const scope = scopeRef.current;
    const startedKey = scopeKeyOf(scope);
    // 결과 반영 직전마다 확인: 이 스캔이 아직 최신(seq)이고, 스코프도 그대로이며, 살아있는가.
    const stillCurrent = () =>
      !disposedRef.current &&
      seq === scanSeqRef.current &&
      scopeKeyOf(scopeRef.current) === startedKey;

    if (!scope.userId || !scope.parseProductName || !scope.orderDate) {
      setScanState("idle");
      setCandidates([]);
      return "idle";
    }

    // 스캔 시작 = 이전 후보 즉시 폐기 — 교체 스캔 중 옛 후보가 actionable로 남지 않는다.
    setScanState("scanning");
    setCandidates([]);

    try {
      const { rows, verified } = await parsedOrderService.getShippingSyncCandidatesByDate(
        scope.userId,
        scope.orderDate
      );
      if (!stillCurrent()) return "stale";
      if (!verified) {
        setScanState("failed");
        return "failed";
      }

      // 복구(reconcile) 경로: 페이지 rows를 DB 확정값으로 재조정 — 응답 유실 커밋의
      // "배너 없음 + 로컬 stale" 조합을 막는다. 존재 id만 패치하는 건 콜백 구현 책임.
      if (options?.reconcile) {
        onReconciledRef.current?.(
          rows.map((r) => ({ parsedOrderId: r.id, productName: r.product_name }))
        );
      }

      if (rows.length === 0) {
        setScanState("ready");
        return "ready";
      }

      const orderIds = Array.from(new Set(rows.map((r) => r.order_id)));
      const { items, verified: itemsVerified } = await orderService.getOrderItemsForSyncScan(orderIds);
      if (!stillCurrent()) return "stale";
      if (!itemsVerified) {
        setScanState("failed");
        return "failed";
      }

      const itemsByOrder = new Map<number, typeof items>();
      for (const item of items) {
        const list = itemsByOrder.get(item.order_id);
        if (list) list.push(item);
        else itemsByOrder.set(item.order_id, [item]);
      }

      const orderIdCounts = new Map<number, number>();
      for (const r of rows) {
        orderIdCounts.set(r.order_id, (orderIdCounts.get(r.order_id) ?? 0) + 1);
      }

      const next: StaleCandidate[] = [];
      for (const r of rows) {
        const orderItems = itemsByOrder.get(r.order_id) ?? [];
        const classification = classifyShippingStale({
          stored: r.product_name,
          parsedModifiedAt: r.modified_at,
          items: orderItems,
        });
        if (classification.kind === "ok" || classification.kind === "not_applicable") {
          continue;
        }

        const zeroItem = classification.kind === "zero_item_with_stored";
        next.push({
          parsedOrderId: r.id,
          orderId: r.order_id,
          nickname: r.nickname ?? "",
          stored: r.product_name,
          synthesized: zeroItem ? "" : classification.synthesized,
          kind: classification.kind,
          defaultChecked:
            classification.kind === "prefix_compatible" &&
            classification.defaultChecked &&
            r.modified_at !== null,
          applyEligible: !zeroItem && r.modified_at !== null,
          modifiedAt: r.modified_at,
          expectedItems: buildExpectedItemsPayload(orderItems),
          duplicateOrderId: (orderIdCounts.get(r.order_id) ?? 0) > 1,
        });
      }

      if (!stillCurrent()) return "stale";
      setCandidates(next);
      setScanState("ready");
      return "ready";
    } catch (error) {
      logError(error as Error, { context: "배송정보 stale 스캔 실패" });
      if (!stillCurrent()) return "stale";
      setScanState("failed");
      setCandidates([]);
      return "failed";
    }
  }, []);

  // 반영 진행 중 도착한 같은 스코프 재스캔 요청(인라인 편집·탭 복귀)의 지연 큐.
  // 즉시 스캔하면 seq가 검증 스캔을 무효화해 "검증 없이 busy 해제"가 된다(델타 리뷰 #2) —
  // 요청만 기록해 두고 apply가 검증 후 busy를 쥔 채로 소화한다.
  const pendingRefreshRef = useRef(false);

  const requestRefresh = useCallback(() => {
    if (applyInFlightRef.current) {
      pendingRefreshRef.current = true;
      return;
    }
    void runScan();
  }, [runScan]);

  // 좌초 방지 소화 루프 — stale-scope 종결 경로에서 큐에 남은 요청을 "현재" 스코프로 소화.
  // 결과값에 영향을 주지 않는 자리(반환 직전)에서만 호출한다.
  const drainPendingRefreshes = useCallback(async (): Promise<void> => {
    while (pendingRefreshRef.current && !disposedRef.current) {
      pendingRefreshRef.current = false;
      await runScan();
    }
  }, [runScan]);

  // 날짜/유저/게이트 변경 시 자동 재스캔. cleanup의 seq 증가가 in-flight 스캔(검증 포함)을
  // 무효화하는 건 "스코프가 실제로 바뀐" 경우에만 정당하다 — refreshToken은 여기 넣지 않는다.
  useEffect(() => {
    void runScan();
    return () => {
      scanSeqRef.current += 1;
    };
  }, [userId, orderDate, parseProductName, runScan]);

  // 같은 스코프 신선도 트리거(인라인 상품명 저장). cleanup 없음 — 검증 스캔을 무효화하면 안 됨.
  // "값이 실제로 바뀌었는가"로 판정 — first-run 플래그는 StrictMode의 이중 effect 셋업을
  // 토큰 변경으로 오인해 dev에서 불필요한 전체 스캔을 만든다(델타 R2 지적).
  const prevRefreshTokenRef = useRef(refreshToken);
  useEffect(() => {
    if (prevRefreshTokenRef.current === refreshToken) return; // 마운트/StrictMode 재셋업
    prevRefreshTokenRef.current = refreshToken;
    requestRefresh();
  }, [refreshToken, requestRefresh]);

  // 탭 복귀 재스캔 (PR 리뷰 P1): "주문 페이지 다른 탭 + 배송엑셀 탭" 병행이 주 사용 패턴이라,
  // 다른 탭에서 바뀐 항목이 같은 스코프의 배너에 반영되도록 visibility 복귀 시 재스캔한다.
  // 반영 중이면 지연 큐로 — 검증/복구 스캔 흐름을 끊지 않고, 끝난 뒤 busy를 쥔 채 소화된다.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        requestRefresh();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [requestRefresh]);

  const rescan = useCallback(() => {
    void runScan();
  }, [runScan]);

  const apply = useCallback(
    async (parsedOrderIds: number[]): Promise<ApplyResult> => {
      // 원자 더블 클릭 가드 — state(isApplying)는 커밋 전 두 호출을 못 막는다(R1 지적).
      if (applyInFlightRef.current) return { type: "stale-scope" };
      applyInFlightRef.current = true;

      const applyScopeKey = scopeKeyOf(scopeRef.current);
      const byId = new Map(candidates.map((c) => [c.parsedOrderId, c]));
      const targets = parsedOrderIds
        .map((id) => byId.get(id))
        .filter(
          (c): c is StaleCandidate =>
            !!c && c.applyEligible && !!c.synthesized && c.modifiedAt !== null
        );
      if (targets.length === 0) {
        applyInFlightRef.current = false;
        return {
          type: "done",
          outcome: { applied: 0, conflicts: 0, failed: 0, appliedRows: [] },
          verifyFailed: false,
        };
      }
      if (targets.length > SHIPPING_SYNC_APPLY_CAP) {
        applyInFlightRef.current = false;
        // RPC 계약(500행 초과 전체 거부)에 걸리기 전에 모달이 막는다 — 여기 도달은 로직 오류.
        throw new Error(`한 번에 반영 가능한 최대 건수는 ${SHIPPING_SYNC_APPLY_CAP}건입니다.`);
      }

      // 스코프 유지 + 미디스포즈를 한 번에 — RPC 후·검증 후·catch에서 모두 재확인한다.
      const applyStillCurrent = () =>
        !disposedRef.current && scopeKeyOf(scopeRef.current) === applyScopeKey;

      setIsApplying(true);
      try {
        const results = await parsedOrderService.applyShippingProductNameSync(
          targets.map((c) => ({
            parsedOrderId: c.parsedOrderId,
            expectedOrderId: c.orderId,
            expectedStored: c.stored,
            expectedModifiedAt: c.modifiedAt as string,
            expectedItems: c.expectedItems,
            newValue: c.synthesized,
          }))
        );

        // 반영 도중 스코프 변경/언마운트 — 이 결과로 UI를 갱신하지 않는다(다른 날짜의 화면,
        // 죽은 컨테이너의 토스트). DB 쓰기는 CAS로 이미 안전하게 끝났고, 새 스코프의 스캔이
        // 실상태를 보여준다.
        if (!applyStillCurrent()) {
          await drainPendingRefreshes();
          return { type: "stale-scope" };
        }

        const statusById = new Map(results.map((r) => [r.parsedOrderId, r.status]));
        const appliedRows = targets
          .filter((c) => statusById.get(c.parsedOrderId) === "applied")
          .map((c) => ({ parsedOrderId: c.parsedOrderId, newValue: c.synthesized }));

        if (appliedRows.length > 0) {
          onAppliedRef.current?.(appliedRows);
        }

        // 검증 스캔(R1-#2): RPC 트랜잭션 "이후" 시작된 항목 변경을 잡는다.
        // busy(isApplying)는 이 스캔이 끝날 때까지 유지 — 다운로드도 그때까지 막힌다.
        // 검증에서 다시 stale이면 후보로 재등장한다.
        let verifyResult = await runScan();

        // 반영 중 지연된 같은 스코프 재스캔 요청(인라인 편집·탭 복귀)을 busy를 쥔 채 소화 —
        // 검증과 신선도 갱신이 모두 끝나기 전에 다운로드가 풀리지 않는다(델타 리뷰 #2).
        while (pendingRefreshRef.current && applyStillCurrent()) {
          pendingRefreshRef.current = false;
          verifyResult = await runScan();
        }

        // 검증 "이후"에도 스코프/생존 재확인 — 옛 스코프의 성공 토스트 차단 (PR2 R2-#3).
        if (!applyStillCurrent()) {
          await drainPendingRefreshes(); // 새 스코프 신선도 요청 좌초 방지
          return { type: "stale-scope" };
        }

        // 검증 스캔이 후보를 재구성하며 RPC의 행별 상태 주석이 사라지므로, 여전히 존재하는
        // 행(=미반영 행)에 한해 다시 붙인다.
        setCandidates((prev) =>
          prev.map((c) => {
            const status = statusById.get(c.parsedOrderId);
            return status && status !== "applied" ? { ...c, applyStatus: status } : c;
          })
        );

        const conflicts = results.filter(
          (r) => r.status === "conflict_stored" || r.status === "conflict_items"
        ).length;
        const failed = results.length - appliedRows.length - conflicts;
        return {
          type: "done",
          outcome: { applied: appliedRows.length, conflicts, failed, appliedRows },
          // 검증이 ready로 끝나지 않았으면(무결성 실패, 또는 예상 밖 supersession) 컨테이너가
          // 모달을 닫아 실패/미확정 상태가 모달 뒤에 숨지 않게 한다. 지연 큐 덕에 같은 스코프
          // 'stale'은 정상 흐름에선 나오지 않는다 — 나오면 안전 방향(닫기)으로.
          verifyFailed: verifyResult !== "ready",
        };
      } catch (error) {
        logError(error as Error, { context: "배송정보 stale 일괄 반영 실패(commit-ambiguous)" });
        // 스코프 변경/언마운트 뒤의 실패 — 옛 스코프를 위한 복구를 하지 않는다.
        // 새 스코프의 effect 스캔이 이미 실상태를 읽고 있다(R2-#3 catch 경로).
        if (!applyStillCurrent()) {
          await drainPendingRefreshes();
          return { type: "stale-scope" };
        }
        // commit-ambiguous: 후보 즉시 폐기 + 복구 스캔(reconcile)으로 페이지 rows까지 동기화.
        setCandidates([]);
        let recoveryResult = await runScan({ reconcile: true });
        // 복구 중 도착한 같은 스코프 refresh 요청을 "결과 확정 전에" busy를 쥔 채 소화 —
        // 종결 결과(recovered)는 마지막으로 적용된 스캔을 반영해야 한다(델타 R3 지적:
        // finally 드레인은 이미 확정된 반환값을 못 바꾼다).
        while (
          pendingRefreshRef.current &&
          !disposedRef.current &&
          applyStillCurrent()
        ) {
          pendingRefreshRef.current = false;
          // recovered를 결정할 수 있는 스캔은 전부 reconcile — 첫 복구가 실패하고 드레인이
          // 성공하는 경우에도 페이지 rows 동기화(onReconciled) 계약이 지켜져야 한다(델타 R4).
          recoveryResult = await runScan({ reconcile: true });
        }
        // 복구/드레인 "이후"에도 재확인 — 대기 중 스코프 변경/언마운트면 이 결과는 옛 화면 것.
        // stale로 밀린 복구를 recovered=false(복구 실패 토스트)로 오보하지 않는다.
        if (!applyStillCurrent() || recoveryResult === "stale") {
          await drainPendingRefreshes(); // 스코프 변경으로 루프를 탈출한 잔여 요청 소화
          return { type: "stale-scope" };
        }
        return { type: "ambiguous", recovered: recoveryResult === "ready" };
      } finally {
        // 반환값은 각 경로에서 드레인 완료 후 확정된다 — finally는 해제만 담당(델타 R3).
        applyInFlightRef.current = false;
        // 언마운트 후 setState 금지 — ref 해제는 무조건, state 갱신은 생존 시에만.
        if (!disposedRef.current) {
          setIsApplying(false);
        }
      }
    },
    [candidates, runScan, drainPendingRefreshes]
  );

  return { scanState, candidates, isApplying, rescan, apply };
}
